import { localText } from "@serenity-is/corelib";

/**
 * Adds a show/hide eye button to every password box on every page: sign-in, sign-up, reset and
 * change password, the user dialog and the settings pages, including boxes that appear later in
 * dialogs. The box goes back to hidden when its form is submitted.
 */
export function initPasswordReveal() {
    if (typeof document === "undefined" || (document as any).passwordRevealReady)
        return;
    (document as any).passwordRevealReady = true;

    const start = () => {
        scan(document.body);
        new MutationObserver(mutations => {
            for (const m of mutations)
                m.addedNodes.forEach(n => n instanceof HTMLElement && scan(n));
        }).observe(document.body, { childList: true, subtree: true });
    };

    if (document.body)
        start();
    else
        document.addEventListener("DOMContentLoaded", start, { once: true });
}

function scan(root: HTMLElement) {
    if (root instanceof HTMLInputElement)
        root.type === "password" && enhance(root);
    else
        root.querySelectorAll<HTMLInputElement>('input[type="password"]').forEach(enhance);
}

const text = (key: string, fallback: string) => localText("Site.PasswordReveal." + key, fallback);

function enhance(input: HTMLInputElement) {
    if (input.dataset.reveal || input.closest(".pw-reveal"))
        return;
    input.dataset.reveal = "1";

    const wrap = document.createElement("span");
    wrap.className = "pw-reveal";
    input.parentNode.insertBefore(wrap, input);
    wrap.appendChild(input);

    const button = document.createElement("button");
    button.type = "button";
    button.className = "pw-reveal-btn";
    const icon = document.createElement("i");
    button.appendChild(icon);
    wrap.appendChild(button);

    const render = () => {
        const hidden = input.type === "password";
        icon.className = hidden ? "fa fa-eye" : "fa fa-eye-slash";
        button.title = hidden ? text("Show", "Show password") : text("Hide", "Hide password");
        button.setAttribute("aria-label", button.title);
        button.setAttribute("aria-pressed", hidden ? "false" : "true");
        button.disabled = input.disabled;
    };

    // keep the caret where it was while the box changes type
    button.addEventListener("mousedown", e => e.preventDefault());
    button.addEventListener("click", () => {
        input.type = input.type === "password" ? "text" : "password";
        render();
        input.focus();
    });

    input.form?.addEventListener("submit", () => {
        input.type = "password";
        render();
    });

    render();
}
